import {
  captureGridHistoryState,
  getGridHistoryScene,
  gridHistorySnapshotsEqual,
} from './gridHistory';
import { GridHistoryCommand } from './Command';
import { cancelPendingGridHistoryCommit } from './gridHistoryCommit';
import { flushPendingGridHistoryCommit } from './gridHistoryFlush';
import { isGridHistoryReady } from './gridHistorySuppress';
import {
  getLastCommittedGridHistory,
  refreshLastCommittedGridHistory,
  setLastCommittedGridHistory,
  clearLastCommittedGridHistory,
} from './gridHistoryBaseline';
import { notifyHistoryUiRefresh } from './gridHistoryUi';

const MAX_HISTORY = 100;

class UndoRedoManager {
  constructor() {
    this.undoStack = [];
    this.redoStack = [];
    this.listeners = new Set();
    this.historyBusy = false;
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notify() {
    this.listeners.forEach((listener) => {
      try {
        listener(this.getState());
      } catch (error) {
        console.warn('UndoRedo listener error:', error);
      }
    });
  }

  getState() {
    const ready = isGridHistoryReady();
    return {
      canUndo: ready && this.undoStack.length > 0,
      canRedo: ready && this.redoStack.length > 0,
      undoCount: ready ? this.undoStack.length : 0,
      redoCount: ready ? this.redoStack.length : 0,
    };
  }

  trimStack() {
    while (this.undoStack.length > MAX_HISTORY) {
      this.undoStack.shift();
    }
  }

  /** Keep baseline aligned with the live editor after each history change. */
  syncBaselineFromCommand(command, mode) {
    if (command?.beforeSnapshot && command?.afterSnapshot) {
      setLastCommittedGridHistory(
        mode === 'undo' ? command.beforeSnapshot : command.afterSnapshot
      );
      return;
    }
    refreshLastCommittedGridHistory(getGridHistoryScene());
  }

  executeCommand(command) {
    if (!isGridHistoryReady()) {
      command.execute();
      return;
    }
    command.execute();
    this.undoStack.push(command);
    this.redoStack = [];
    this.trimStack();
    this.syncBaselineFromCommand(command, 'execute');
    this.notify();
  }

  recordSnapshot(command) {
    if (!isGridHistoryReady()) return;
    this.undoStack.push(command);
    this.redoStack = [];
    this.trimStack();
    this.syncBaselineFromCommand(command, 'execute');
    this.notify();
  }

  /**
   * Record a full scene+redux step (flushes any pending debounced edit first).
   */
  recordGridHistoryTransition(beforeSnapshot, scene, label = 'edit') {
    flushPendingGridHistoryCommit(
      (before, targetScene, commitLabel) =>
        this.recordGridHistoryTransitionCore(before, targetScene, commitLabel),
      label
    );
    const before = beforeSnapshot || getLastCommittedGridHistory();
    this.recordGridHistoryTransitionCore(before, scene, label);
  }

  /** Internal: record one step without flushing pending edits. */
  recordGridHistoryTransitionCore(beforeSnapshot, scene, label = 'edit') {
    if (!isGridHistoryReady() || !scene) return;

    const before = beforeSnapshot || getLastCommittedGridHistory();
    if (!before) {
      refreshLastCommittedGridHistory(scene);
      return;
    }

    const afterSnapshot = captureGridHistoryState(scene);
    if (gridHistorySnapshotsEqual(before, afterSnapshot)) return;

    this.recordSnapshot(
      new GridHistoryCommand(before, afterSnapshot, label)
    );
  }

  /** Record current editor state vs last baseline (no explicit before needed). */
  recordFromBaseline(scene, label = 'edit') {
    this.recordGridHistoryTransition(null, scene || getGridHistoryScene(), label);
  }

  async undo() {
    if (this.historyBusy || this.undoStack.length === 0) return;
    if (!isGridHistoryReady()) return;

    this.historyBusy = true;
    cancelPendingGridHistoryCommit();
    const command = this.undoStack.pop();
    try {
      await command.undo();
      this.redoStack.push(command);
      this.syncBaselineFromCommand(command, 'undo');
      notifyHistoryUiRefresh();
    } catch (error) {
      console.warn('Undo failed:', error);
      this.undoStack.push(command);
    } finally {
      this.historyBusy = false;
      this.notify();
    }
  }

  async redo() {
    if (this.historyBusy || this.redoStack.length === 0) return;
    if (!isGridHistoryReady()) return;

    this.historyBusy = true;
    cancelPendingGridHistoryCommit();
    const command = this.redoStack.pop();
    try {
      await command.execute();
      this.undoStack.push(command);
      this.syncBaselineFromCommand(command, 'execute');
      notifyHistoryUiRefresh();
    } catch (error) {
      console.warn('Redo failed:', error);
      this.redoStack.push(command);
    } finally {
      this.historyBusy = false;
      this.notify();
    }
  }

  clear() {
    cancelPendingGridHistoryCommit();
    this.undoStack = [];
    this.redoStack = [];
    clearLastCommittedGridHistory();
    this.notify();
  }
}

const undoRedoManager = new UndoRedoManager();

export default undoRedoManager;
